<template>
    <div
        class="ola-header-wrapper"
        :class="{ 
        'white-bg': isAfterBanner || isMobileMenuOpen,
        'mobile-menu-open': isMobileMenuOpen,
        'training-header': isTrainingPage
        }"
        :style="[{ height: '68px' }, rootStyle]"
    >
        <div class="ola-header-content">
            <div class="ola-header-content-left">
                <div class="ola-logo-dual" :class="{ 'show-white': showWhiteLogo }">
                    <a href="/" class="logo-link">
                        <span v-once class="logo-layer logo-default" :style="{ backgroundImage: `url(${logoDefaultHref})` }"></span>
                        <span v-once class="logo-layer logo-white" :style="{ backgroundImage: `url(${logoWhiteHref})` }"></span>
                    </a>
                </div>
                
                <!-- 桌面端菜单 - 从配置动态渲染 -->
                <ola-menu mode="horizontal" class="desktop-menu">
                    <template v-for="item in navigation" :key="item.index">
                        <!-- 有子菜单的项 -->
                        <ola-sub-menu v-if="item.children" :index="item.index">
                            <template #title>{{ item.title }}</template>
                            <ola-menu-item 
                                v-for="child in item.children" 
                                :key="child.index"
                                :index="child.index" 
                                :href="child.href"
                                :target="child.target"
                            >{{ child.title }}</ola-menu-item>
                    </ola-sub-menu>
                        <!-- 普通菜单项 -->
                        <ola-menu-item 
                            v-else 
                            :index="item.index" 
                            :href="item.href"
                            :target="item.target"
                        >{{ item.title }}</ola-menu-item>
                    </template>
                </ola-menu>
            </div>
            <div class="ola-header-right">
                <Login />
                <div class="mobile-menu-toggle" @click="toggleMobileMenu">
                    <i class="iconfont">&#xe69f;</i>
                </div>
            </div>
        </div>
        
        <!-- 移动端菜单 - 从配置动态渲染 -->
        <div class="mobile-menu" :class="{ 'mobile-menu-active': isMobileMenuOpen }">
            <ola-menu mode="vertical">
                <template v-for="item in navigation" :key="item.index">
                    <!-- 有子菜单的项 -->
                    <ola-sub-menu v-if="item.children" :index="item.index">
                        <template #title>{{ item.title }}</template>
                        <ola-menu-item 
                            v-for="child in item.children" 
                            :key="child.index"
                            :index="child.index" 
                            :href="child.href"
                            :target="child.target"
                        >{{ child.title }}</ola-menu-item>
                </ola-sub-menu>
                    <!-- 普通菜单项 -->
                    <ola-menu-item 
                        v-else 
                        :index="item.index" 
                        :href="item.href"
                        :target="item.target"
                    >{{ item.title }}</ola-menu-item>
                </template>
            </ola-menu>
        </div>
    </div>
</template>
<script setup>
import Login from '@/pages/components/Login/index.vue'
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { getImagePath } from '@/utils/ImageHelper'
import { useSiteConfigStore } from '@/stores/SiteConfig'

// 获取站点配置
const siteConfigStore = useSiteConfigStore()

/**
 * Header 主题色（可配置）
 * - 默认值：#FF1F59
 * - 可通过站点配置 `headerMainColor` 覆盖（如德国站 #F93636）
 */
const headerMainColor = computed(() => siteConfigStore.headerMainColor || '#FF1F59')

/**
 * 根节点样式变量（供样式使用）
 */
const rootStyle = computed(() => ({
    '--header-main-color': headerMainColor.value
}))

// 从配置中获取导航菜单数据
const navigation = computed(() => {
  return siteConfigStore.navigation || []
})

// 创建响应式变量跟踪是否已滚动超过轮播图
const isAfterBanner = ref(false)
// 移动菜单是否打开
const isMobileMenuOpen = ref(false)
const route = useRoute()

// 添加计算属性以确保路由变化时能够更新类名
const isTrainingPage = computed(() => {
    return route.path === '/help/classroom'
})

// 是否显示白色版 logo（不改变图片 src，只改变层的可见性）
const showWhiteLogo = computed(() => route.path === '/training' && !isAfterBanner.value)

// 预加载首屏 logo，避免刷新时从无到有的闪烁
const logoDefaultHref = computed(() => getImagePath('logo.png'))
const logoWhiteHref = computed(() => getImagePath('logo-white.png'))

const cdnLinks = computed(() => {
    const links = []
    const cfg = siteConfigStore
    if (cfg?.useCdn && cfg?.cdnPrefix) {
        try {
            const u = new URL(cfg.cdnPrefix)
            const origin = `${u.protocol}//${u.host}`
            links.push({ rel: 'dns-prefetch', href: origin })
            links.push({ rel: 'preconnect', href: origin, crossorigin: '' })
        } catch (e) {
            // ignore bad url
        }
    }
    return links
})

useHead({
    link: [
        ...cdnLinks.value,
        { rel: 'preload', as: 'image', href: logoDefaultHref, fetchpriority: 'high', crossorigin: '' },
        { rel: 'preload', as: 'image', href: logoWhiteHref, fetchpriority: 'high', crossorigin: '' }
    ]
})

// 滚动事件处理函数
// const handleScroll = () => {
//     // 获取轮播图元素
//     const carousel = document.querySelector('.ola-media-wrap')
//     if (carousel) {
//         // 获取轮播图的高度
//         const carouselHeight = carousel.offsetHeight
//         // 当前滚动位置
//         const scrollTop = window.scrollY || document.documentElement.scrollTop
//         // 判断是否已滚动超过轮播图
//         isAfterBanner.value = scrollTop > carouselHeight
//     } else {
//         // 如果页面中没有轮播图元素，则默认使用白色背景
//         isAfterBanner.value = true
//     }
// }
const handleScroll = () => {
    const headerBar = document.querySelector('.ola-header-wrapper')
    if (headerBar) {
        const headerBarHeight = headerBar.offsetHeight
        // 当前滚动位置
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        // 判断是否已滚动超过指定区域
        isAfterBanner.value = scrollTop > headerBarHeight
    } else {
        isAfterBanner.value = true
    }
}
// 切换移动菜单
const toggleMobileMenu = (event) => {
    // 阻止事件冒泡，防止点击事件传递到其他元素
    event.stopPropagation()
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    // 当菜单打开时，禁止页面滚动
    document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
}

// 监听路由变化，关闭移动菜单
watch(() => route.path, () => {
    if (isMobileMenuOpen.value) {
        isMobileMenuOpen.value = false
        document.body.style.overflow = ''
    }
})

// 组件挂载时添加滚动监听
onMounted(() => {
    // 初始检查位置
    handleScroll()
    // 添加滚动事件监听
    window.addEventListener('scroll', handleScroll)
})

// 组件卸载时移除滚动监听
onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    // 确保body的overflow被重置
    document.body.style.overflow = ''
})
</script>
<style lang="scss">
@use "@/assets/styles/common/responsive" as responsive;

// 定义移动端断点
$mobile-breakpoint: 1024px;

.ola-logo {
    margin-right: 22px;
}

/* 双层 Logo 叠放，避免切换时替换图片源导致闪烁 */
.ola-logo-dual {
    position: relative;
    width: 139px;
    height: 50px;
    /* 避免强制创建复合层导致的初次闪动 */
}
.ola-logo-dual .logo-layer {
    position: absolute;
    inset: 0;
    /* 不使用透明度过渡，避免复合层切换 */
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    display: block;
    visibility: visible;
}
.ola-logo-dual .logo-link { display: block; width: 139px; height: 50px; }
.ola-logo-dual .logo-default { z-index: 2; }
.ola-logo-dual .logo-white { z-index: 1; }
.ola-logo-dual.show-white .logo-default {visibility: hidden; }
.ola-logo-dual.show-white .logo-white { z-index: 2; }
.logo-link:focus-visible,
.logo-link:focus {
    outline: none;
}


.ola-header-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 68px;
    z-index: 1000;
    transition: background-color 0.3s, box-shadow 0.3s, color 0.3s;

    // 默认透明背景
    background-color: transparent;

    // 默认状态下菜单文字为白色
    .ola-menu-item,
    .ola-submenu-title {
        font-weight: 400;
        transition: color 0.3s;
    }
     .ola-menu-item-content:hover{
            color: var(--header-main-color, #FF1F59);
     }

    // 默认状态下按钮样式
    .ola-btn-login {
        color: #fff;
        border-color: #fff;
        transition: all 0.3s;
        position: relative; // 添加相对定位
        z-index: 1002; // 确保按钮在最上层

        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }
    }
    .ola-submenu-opened:hover {
         .ola-submenu-title-content{
            color: var(--header-main-color, #FF1F59);
        }
        .ola-submenu-arrow::after{
            border-color: var(--header-main-color, #FF1F59);
        }
    }
    
    // 滚动超过轮播图后的样式
    &.white-bg {
        background-color: #fff;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

        .ola-menu-item-content,
        .ola-submenu-title-content {
            color: #333;
        }
        .ola-menu-item-content:hover,.ola-submenu-title-content:hover{
            color: var(--header-main-color, #FF1F59);
        }
        // 滚动后菜单文字为黑色
        .ola-menu-item,
        .ola-submenu-title,
        .user-login a,
        .welcome {
            color: #333;
        }

        .ola-menu-horizontal .ola-submenu-inner {
            background-color: white;
        }

        // 滚动后按钮样式
        .ola-btn-login {
            color: #333;
            border-color: #333;

            &:hover {
                background-color: rgba(0, 0, 0, 0.05);
            }
        }
        
        // 移动菜单按钮在白色背景下为黑色
        .mobile-menu-toggle .iconfont {
            color: #333;
        }
    }
    &.training-header {
        box-shadow: none;
        background: linear-gradient(114deg, #F9F9FA 12.82%, #F5F9FE 100%);
    }

    

    .ola-header-content {
        margin: 0 auto;
        display: flex;
        position: relative;
        align-items: center;
        justify-content: space-between;
        width: 1200px;
        height: 68px;
        z-index: 1000;

        @include responsive.ola-respond-below(xl) {
            width: 100%;
            padding: 0 20px;
        }

        @include responsive.ola-respond-below(lg) {
            padding: 0 15px;
        }

        @include responsive.ola-respond-below(md) {
            padding: 0 10px;
        }

        .ola-header-content-left {
            display: flex;
            align-items: center;
            position: relative;
        }
        
        .ola-header-right {
            display: flex;
            align-items: center;
            gap: 15px; // 添加间距
            position: relative; // 添加相对定位
            z-index: 1002; // 确保在移动菜单之上
        }
    }

    // 移动菜单按钮样式
    .mobile-menu-toggle {
        display: none;
        position: relative;
        top:2px;
        cursor: pointer;
        z-index: 1001;
        
        .iconfont {
          font-size: 20px;
        }
    }
    
    
    // 移动菜单样式
    .mobile-menu {
        display: none;
        position: fixed;
        top: 68px;
        left: 0;
        width: 100%;
        height: calc(100vh - 68px);
        background-color: #fff;
        z-index: 900;
        padding: 20px;
        overflow-y: auto;
        transform: translateY(-100%);
        opacity: 0;
        transition: transform 0.3s ease, opacity 0.3s ease;
        
        &.mobile-menu-active {
            transform: translateY(0);
            opacity: 1;
        }
        .ola-menu-item-link {
            padding-left: 0;
        }
        .ola-menu-vertical .ola-submenu-content{
            border: none;
        }
        .ola-submenu-title,.ola-menu-item {
            border-bottom: 1px solid #F0F0F0;
        }
        .ola-menu-item:last-child,.ola-submenu-content .ola-menu-item{
            border-bottom: none;
        }
        .ola-menu {
            border-right: none;
            
            .ola-menu-item,
            .ola-submenu-title {
                color: #333;
                height: 50px;
                line-height: 50px;
            }
            
            .ola-submenu-content {
                padding-left: 0px;
            }
        }
    }

    .ola-btn-lg.ola-btn-round {
        width: 112px;
        height: 36px;
        border-radius: 20px;

        @include responsive.ola-respond-below(md) {
            width: 100px;
            height: 32px;
            font-size: 14px;
        }
    }
    
    // 移动端适配
    @media (max-width: $mobile-breakpoint) {
        .desktop-menu {
            display: none;
        }
        
        .mobile-menu-toggle {
            display: block;
        }
        
        .mobile-menu {
            display: block;
        }
        
        // 移除移动端登录按钮样式
        .mobile-login {
            display: none;
        }
    }
}

.ola-header-placeholder {
    height: 68px;
}

/* 平板设备上的菜单项调整 */
@include responsive.ola-respond-between(sm, md) {

    .ola-submenu-title,
    .ola-menu-item {
        padding: 0 10px !important;
    }

    .ola-logo {
        margin-right: 10px;
    }
}

/* 平板触摸模式特别优化 */
@media (pointer: coarse) {

    .ola-submenu-title,
    .ola-menu-item {
        /* 增加触摸目标区域 */
        // padding: 0 15px !important;

        @include responsive.ola-respond-below(md) {
            // padding: 0 5px !important;
        }
    }

    /* 为子菜单添加明显的边框，便于识别 */
    .ola-menu-horizontal .ola-submenu-content {
        border: 1px solid #eaeaea !important;
    }

    /* 确保在iPad上能正确显示子菜单 */
    .ola-menu-horizontal .ola-submenu-content .ola-menu-item {
        height: auto !important;
        min-height: 40px !important;
        line-height: 40px !important;
        padding: 0 15px !important;

        @include responsive.ola-respond-below(md) {
            padding: 0 5px !important;
        }

        text-align: center;
    }
}

/* 手动添加特定设备样式修复 */
@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {

    /* iPad特定修复 */
    .ola-submenu-title {
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
    }

    /* 解决overflow-x导致的子菜单被遮挡问题 */
    .ola-header-wrapper .ola-header-content .ola-header-content-left {
        overflow: visible !important;
    }

    .ola-menu-horizontal .ola-submenu-content {
        display: block;
        opacity: 1;
        visibility: visible;
        /* 确保子菜单绝对定位不受父容器overflow影响 */
        position: absolute;
        z-index: 1000;

        /* 改进子菜单样式 */
        border: 1px solid #eee !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
        min-width: 140px !important;
        padding: 8px 0 !important;
        left: 50% !important;
        transform: translateX(-50%) !important;
    }

    /* 优化子菜单定位 */
    .ola-menu-horizontal .ola-submenu {
        position: static;

        &:hover .ola-submenu-content,
        &.ola-submenu-opened .ola-submenu-content {
            display: block !important;
        }
    }

    /* 子菜单项样式优化 */
    .ola-menu-horizontal .ola-submenu-content .ola-menu-item {
        padding: 0 15px !important;
        height: 36px !important;
        line-height: 36px !important;
        min-height: 36px !important;
        white-space: nowrap;
        font-size: 14px;

        @include responsive.ola-respond-below(md) {
            padding: 0 5px !important;
        }
    }
}
</style>